import { Injectable } from '@angular/core';
import { isDevMode } from '@angular/core';
import { Mixpanel, MixpanelPeople } from '@awesome-cordova-plugins/mixpanel';
import { Platform } from '@ionic/angular';
import { environment } from "../../environments/environment";
//import { Appsflyer } from '@awesome-cordova-plugins/appsflyer/ngx';
//import { Adjust, AdjustConfig, AdjustEvent } from '@awesome-cordova-plugins/adjust/ngx';
import { Purchases } from '@revenuecat/purchases-capacitor';
import { FirebaseAnalytics } from '@capacitor-firebase/analytics';
import { BranchIo } from '@awesome-cordova-plugins/branch-io';
import posthog from 'posthog-js'
import logRocket from 'logrocket';
import { FirebaseInitService } from './firebase-init.service';

declare let window: any;
declare let cordova; 

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  constructor(
    private platform: Platform,
    //public appsflyer: Appsflyer,
    //public adjust: Adjust,
    private firebaseInit: FirebaseInitService
    ) { 
  }

  async init()
  {
    console.log('Starting Analytics initialization...');
    
    // Wait for Firebase to be fully initialized
    await this.firebaseInit.waitForReady();
    
    console.log('Firebase is ready, configuring Analytics...');
    
    if(isDevMode())
    {
      //Disable Firebase Analytics in dev mode
      console.log('Development mode detected, disabling Analytics...');
      if (this.platform.is("capacitor") || this.platform.is("cordova")) {
        await this.firebaseInit.safeFirebaseCall(
          () => FirebaseAnalytics.setEnabled({ enabled: false })
        );
      } else {
        // Note: setAnalyticsCollectionEnabled is not available in modular API
        // Analytics collection is controlled via Firebase console
        console.log("Analytics collection disabled via Firebase console");
      }
    }
    if (this.platform.is("capacitor") || this.platform.is("cordova"))
    {
      if(this.platform.is("ios"))
      {
        window.cordova.exec(suc, err, "idfa", "requestPermission", []);
        function suc(res: any) {
          console.log("Successfully enabled app tracking: " + JSON.stringify(res))
        }
        function err(res: any ) {
          console.log("Error while enabling app tracking: " + JSON.stringify(res))
        }
      }

      //Mixpanel init
      try {
        await Mixpanel.init(environment.mixpanel);
        console.log('✅ Mixpanel initialized successfully');
      } catch (mixpanelError: any) {
        console.error('❌ Mixpanel initialization failed:', mixpanelError?.message || mixpanelError);
        // Continue with other analytics initialization even if Mixpanel fails
      }

      //Branch SDK
      await BranchIo.initSession();
      let firstParams = await BranchIo.getFirstReferringParams()
      console.log("BRANCH FIRST REFERRER" + firstParams);
      //alert("BRANCH FIRST REFERRER" + firstParams);

        //Singular init
        /*let singularConfig = cordova.plugins.SingularCordovaSdk.SingularConfig(environment.singular.key, "secretKey");
        singularConfig.withLogLevel(cordova.plugins.SingularCordovaSdk.SingularLogLevel.SingularLogLevelDebug);
        singularConfig.withFacebookAppId("2304902353021830"); //enable META Install Referrer

        cordova.plugins.SingularCordovaSdk.init(singularConfig);*/
        //this.adjustInit();
        //await this.appsflyer.initSdk(environment.appsflyer);
      //Purchases.collectDeviceIdentifiers();
    }
    
    console.log('Analytics initialization completed');
  }

  public async trackEvent(name: string, properties?: any) 
  {
    try {
      if(this.platform.is("capacitor") || this.platform.is("cordova"))
      {
        await Mixpanel.track(name, properties);
      }
      posthog.capture(name, properties);
    } catch (error) {
      console.warn("Analytics trackEvent error:", error);
    }
  }

  public async MPTrackTime(name: string) 
  {
    if(this.platform.is("capacitor") || this.platform.is("cordova"))
    {
      await Mixpanel.timeEvent(name);
    }
  }

  public async setIdentity(uid: string)
  {
    try {
      if (this.platform.is("capacitor") || this.platform.is("cordova")) {
        await BranchIo.setIdentity(uid);
        try {
          await Mixpanel.identify(uid);
        } catch (mixpanelError: any) {
          console.warn("Mixpanel identify error (may not be initialized):", mixpanelError?.message || mixpanelError);
        }
      }
      posthog.identify(uid);
      logRocket.identify(uid);
    } catch (error) {
      console.warn("Analytics setIdentity error:", error);
    }
  }

  public async signOut()
  {
    if (this.platform.is("capacitor") || this.platform.is("cordova")) {
      await BranchIo.logout();
      try {
        await Mixpanel.reset();
      } catch (mixpanelError: any) {
        console.warn("Mixpanel reset error (may not be initialized):", mixpanelError?.message || mixpanelError);
      }
    }
    posthog.reset();
  }

  public async MPOptOut()
  {
    //ToDo: Opt out from Mixpanel
  }

  public async MPDeleteUser()
  {
    await MixpanelPeople.deleteUser();
  }

  public async MPSetSuperProps(properties: any)
  {
    await Mixpanel.registerSuperProperties(properties);
  }

  public async GASetScreenName(name: string)
  {
    if(this.platform.is("capacitor") || this.platform.is("cordova"))
    {
      return await this.firebaseInit.safeFirebaseCall(
        () => FirebaseAnalytics.setCurrentScreen({screenName: name})
      );
    }
    else {
      try {
        // Use modular Firebase API for web
        const { getApp } = await import('firebase/app');
        const { getAnalytics, logEvent } = await import('firebase/analytics');
        const app = getApp();
        const analytics = getAnalytics(app);
        await logEvent(analytics, 'screen_view', {
          screen_name: name as any
        } as any);
        console.log("Screen view tracked:", name);
      } catch (error) {
        console.error("Error tracking screen view:", error);
      }
    }
  }

  public async GAOptOut()
  {
    if(this.platform.is("capacitor") || this.platform.is("cordova")) {
      return await this.firebaseInit.safeFirebaseCall(
        () => FirebaseAnalytics.setEnabled({ enabled: false })
      );
    } else {
      try {
        // Use modular Firebase API for web
        const { getApp } = await import('firebase/app');
        const { getAnalytics, setAnalyticsCollectionEnabled } = await import('firebase/analytics');
        const app = getApp();
        const analytics = getAnalytics(app);
        await setAnalyticsCollectionEnabled(analytics, false);
        console.log("Analytics collection disabled for web");
      } catch (error) {
        console.error("Error disabling analytics:", error);
      }
    }
  }

  public async GAOptIn()
  {
    if(this.platform.is("capacitor") || this.platform.is("cordova")) {
      return await this.firebaseInit.safeFirebaseCall(
        () => FirebaseAnalytics.setEnabled({ enabled: true })
      );
    } else {
      try {
        // Use modular Firebase API for web
        const { getApp } = await import('firebase/app');
        const { getAnalytics, setAnalyticsCollectionEnabled } = await import('firebase/analytics');
        const app = getApp();
        const analytics = getAnalytics(app);
        await setAnalyticsCollectionEnabled(analytics, true);
        console.log("Analytics collection enabled for web");
      } catch (error) {
        console.error("Error enabling analytics:", error);
      }
    }
  }
  
  //Adjust
  /*public adjustInit()
  {
    if(this.platform.is("ios"))
    {
      const config = new AdjustConfig(environment.adjust.iosToken, environment.adjust.mode);
      this.adjust.create(config);
      config.setLogLevel(environment.adjust.logmode);
    }
    else if(this.platform.is("android")) {
      const config = new AdjustConfig(environment.adjust.androidToken, environment.adjust.mode);
      this.adjust.create(config);
      config.setLogLevel(environment.adjust.logmode);
    }
  }*/

  /*public async setAppsFlyerID() {
    console.log("SETAPPSFLYER ID; ID: " + await this.appsflyer.getAppsFlyerUID());
    return Purchases.setAppsflyerID(await this.appsflyer.getAppsFlyerUID());
  }*/

  /*public async setAdjustID() {
    console.log("SETADJUST ID; ID: " + await this.adjust.getAdid())
    return Purchases.setAdjustID({adjustID: await this.adjust.getAdid()});
  }*/

  public async trackConversion(name: string, value: number) {
    if (this.platform.is("capacitor") || this.platform.is("cordova")) {
      BranchIo.sendBranchEvent(name, {revenue: value});
    }
    /*let event: AdjustEvent = new AdjustEvent(name);
    event.setRevenue(value, "EUR");
    return this.adjust.trackEvent(event);*/
  }

  public async branchTrackEvent(name: string, metadata: any) {
    if (this.platform.is("capacitor") || this.platform.is("cordova")) {
      return await BranchIo.sendBranchEvent(name, metadata);
    }
    return Promise.resolve();
  }

  public async branchSetReqMetData() {
    if (this.platform.is("capacitor") || this.platform.is("cordova")) {
      return await BranchIo.setRequestMetadata();
    }
    return Promise.resolve();
  }

  public postHogEvent(name: string, properties?: any)
  {
    posthog.capture(name, properties);
  }

  public postHogSurvey(id: string, selector: any)
  {
    posthog.renderSurvey(id, selector);
  }   
  
  public setRevCatEmail(email: string)
  {
    if(email != undefined || email != "")
    {
      Purchases.setEmail({email});
    }
  }
  
  public async setRevCatCampaignData()
  {
    await Purchases.collectDeviceIdentifiers();
    if (this.platform.is("capacitor") || this.platform.is("cordova")) {
      let attribution = await BranchIo.getFirstReferringParams();
      if(attribution && attribution != null && attribution != undefined)
      {
        let channel = attribution["~channel"];
        let campaign = attribution["~campaign"];
        console.log("attrib" + attribution["~channel"] + " " + attribution["~campaign"]);
        //console.log("BRANCH FIRST REFERRER" + channel + " " + campaign);
        await Purchases.setMediaSource({mediaSource: channel});
        await Purchases.setCampaign({campaign: campaign});
        await Purchases.setCreative({creative: attribution["+referrer"]});
        await this.MPSetSuperProps(
          {
          "network": channel,
          "campaign": campaign}
        );
      }
    }

    /*let attribution = await this.adjust.getAttribution();
    //console.log("ATTRIBUTION " + JSON.stringify(attribution))
    Purchases.setMediaSource({mediaSource: attribution.network});
    Purchases.setCampaign({campaign: attribution.campaign})
    Purchases.setAdGroup({adGroup: attribution.adgroup});
    await this.MPSetSuperProps(
      {"adjustID": await this.adjust.getAdid(),
      "network": attribution.network,
      "campaign": attribution.campaign}
    );
    Purchases.setPushToken({pushToken: localStorage.getItem("pushToken")});*/
  }
}
